import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import { ApiError, UnexpectedResponseError } from '@/shared/api/http';
import { getSession, refreshSession, subscribeSession } from '@/shared/auth';

/**
 * Создаёт клиент запросов с общими для всего приложения правилами.
 *
 * Клиент создаётся внутри компонента, а не в модуле: модульный экземпляр
 * пережил бы горячую перезагрузку и утащил в неё устаревший кеш.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Живые обновления придут по WebSocket (фаза 2), а не перезапросом
        // при каждом возврате в окно.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // Повторять запрос, на который сервер осознанно ответил «нельзя» или
          // «не найдено», бессмысленно: ответ не изменится, а пользователь
          // будет ждать. Повторяем только сетевые сбои и ошибки сервера.
          if (error instanceof ApiError && error.status < 500) {
            return false;
          }

          // Запрос ушёл не по адресу — повторы это не исправят, адрес нужно
          // чинить в настройках окружения.
          if (error instanceof UnexpectedResponseError) {
            return false;
          }

          return failureCount < 2;
        },
      },
    },
  });
}

function currentUserID(): string {
  const session = getSession();

  return session.status === 'authenticated' ? session.user.id : session.status;
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);

  // Токен доступа живёт в памяти вкладки и умирает вместе с ней, а вот cookie
  // с токеном обновления переживает перезагрузку. Поэтому при запуске
  // приложение один раз пробует восстановить сеанс: без этого F5 выкидывал бы
  // из системы, хотя войти человек не переставал.
  useEffect(() => {
    void refreshSession();
  }, []);

  // Смена того, кто вошёл, стирает кеш запросов целиком.
  //
  // Без этого следующий человек несколько секунд смотрит на данные
  // предыдущего: данные из кеша считаются свежими (staleTime), поэтому
  // рисуются сразу, а перезапрос приходит позже. На общем компьютере это
  // утечка чужих данных.
  useEffect(() => {
    let previous = currentUserID();

    return subscribeSession(() => {
      const next = currentUserID();

      if (next !== previous) {
        previous = next;
        queryClient.clear();
      }
    });
  }, [queryClient]);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
